/**
 * aclAdminService — thin axios wrappers for the ACL admin API.
 *
 * Covers every route in `api/src/endpoints/acl_admin.py`:
 *   - Roles + capabilities catalog + integrity report
 *   - Role-capability templates
 *   - Tenant materialization (per-role / per-tenant overrides)
 *   - Drift / propagation
 *   - Grant authority
 *   - Account overrides + effective capabilities
 *   - Audit log (SAFRS JSON:API)
 *
 * Conventions (matches accountService.js):
 *   - Raw `api` (default import) — no apiCall helper.
 *   - Methods return the axios promise. Callers `await` and read `.data`.
 *   - No try/catch — errors propagate to the caller.
 *   - DELETE-with-body uses axios `{ data: body }` config form when a
 *     reason (or other body) is provided. Bare DELETE otherwise.
 *   - Auth header is automatic via the axios interceptor (`@/axios`).
 *
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 */
import api from './api'

// ---------------------------------------------------------------------------
// Roles + capabilities catalog + integrity
// ---------------------------------------------------------------------------

/**
 * Full integrity report on the ACL graph (roles, capabilities, grants).
 * @returns {Promise<AxiosResponse<object>>}
 */
export function getIntegrityReport() {
    return api.get('/admin/acl/integrity')
}

/**
 * List all capability rows.
 * @returns {Promise<AxiosResponse<{capabilities: object[]}>>}
 */
export function listCapabilities() {
    return api.get('/admin/acl/capabilities')
}

/**
 * List all role rows.
 * @returns {Promise<AxiosResponse<{roles: object[]}>>}
 */
export function listRoles() {
    return api.get('/admin/acl/roles')
}

/**
 * Create a new role.
 * @param {object} body  Role payload (`{name, reason, ...}`).
 * @returns {Promise<AxiosResponse<{role: object}>>}
 */
export function createRole(body) {
    return api.post('/admin/acl/roles', body)
}

/**
 * Patch an existing role.
 * @param {number|string} id    Role id.
 * @param {object}        body  Patch payload (`{name?, reason}`).
 * @returns {Promise<AxiosResponse<{role: object}>>}
 */
export function patchRole(id, body) {
    return api.patch(`/admin/acl/roles/${id}`, body)
}

/**
 * Delete a role.
 * @param {number|string} id    Role id.
 * @param {object}        [body]  Optional `{reason}` payload.
 * @returns {Promise<AxiosResponse<void>>}
 */
export function deleteRole(id, body = undefined) {
    return body === undefined
        ? api.delete(`/admin/acl/roles/${id}`)
        : api.delete(`/admin/acl/roles/${id}`, { data: body })
}

// ---------------------------------------------------------------------------
// Role-capability templates
// ---------------------------------------------------------------------------

/**
 * Get the template capability set for a role.
 * @param {number|string} roleId
 * @returns {Promise<AxiosResponse<{role_id: number, capabilities: string[]}>>}
 */
export function getRoleTemplateCapabilities(roleId) {
    return api.get(`/admin/acl/roles/${roleId}/capabilities`)
}

/**
 * Replace the template capability set for a role.
 * @param {number|string} roleId
 * @param {object} body  `{capabilities: string[], reason: string}`
 * @returns {Promise<AxiosResponse<{role_id: number, capabilities: string[]}>>}
 */
export function putRoleTemplateCapabilities(roleId, body) {
    return api.put(`/admin/acl/roles/${roleId}/capabilities`, body)
}

/**
 * Add a single capability to a role's template.
 * @param {number|string} roleId
 * @param {number|string} capId
 * @param {object}        [body]  Optional `{reason}` payload.
 * @returns {Promise<AxiosResponse<{role_id: number, capability_id: number}>>}
 */
export function addRoleTemplateCapability(roleId, capId, body = undefined) {
    return body === undefined
        ? api.post(`/admin/acl/roles/${roleId}/capabilities/${capId}`)
        : api.post(`/admin/acl/roles/${roleId}/capabilities/${capId}`, body)
}

/**
 * Remove a single capability from a role's template.
 * @param {number|string} roleId
 * @param {number|string} capId
 * @param {object}        [body]  Optional `{reason}` payload.
 * @returns {Promise<AxiosResponse<void>>}
 */
export function removeRoleTemplateCapability(roleId, capId, body = undefined) {
    return body === undefined
        ? api.delete(`/admin/acl/roles/${roleId}/capabilities/${capId}`)
        : api.delete(`/admin/acl/roles/${roleId}/capabilities/${capId}`, { data: body })
}

// ---------------------------------------------------------------------------
// Tenant materialization
// ---------------------------------------------------------------------------

/**
 * Get the effective capability set for (role, tenant). May be the role
 * template (if not materialized) or a tenant-specific override.
 * @param {number|string} roleId
 * @param {number|string} tenantId
 * @returns {Promise<AxiosResponse<{role_id: number, tenant_id: number, materialized: boolean, capabilities: string[]}>>}
 */
export function getEffectiveCapabilitiesForRoleTenant(roleId, tenantId) {
    return api.get(`/admin/acl/roles/${roleId}/effective-capabilities/${tenantId}`)
}

/**
 * Materialize / replace a tenant-specific capability set for a role.
 * @param {number|string} roleId
 * @param {number|string} tenantId
 * @param {object} body  `{capabilities: string[], reason: string}`
 * @returns {Promise<AxiosResponse<{role_id: number, tenant_id: number, materialized: boolean, capabilities: string[]}>>}
 */
export function putTenantRoleMaterialization(roleId, tenantId, body) {
    return api.put(`/admin/acl/roles/${roleId}/effective-capabilities/${tenantId}`, body)
}

/**
 * Drop a tenant-specific materialization, falling back to the role template.
 * @param {number|string} roleId
 * @param {number|string} tenantId
 * @param {object}        [body]  Optional `{reason}` payload.
 * @returns {Promise<AxiosResponse<void>>}
 */
export function deleteTenantRoleMaterialization(roleId, tenantId, body = undefined) {
    return body === undefined
        ? api.delete(`/admin/acl/roles/${roleId}/effective-capabilities/${tenantId}`)
        : api.delete(`/admin/acl/roles/${roleId}/effective-capabilities/${tenantId}`, { data: body })
}

// ---------------------------------------------------------------------------
// Drift / propagation
// ---------------------------------------------------------------------------

/**
 * Rows describing capabilities present on a role template but missing
 * from one or more tenant materializations.
 * @returns {Promise<AxiosResponse<{rows: object[]}>>}
 */
export function getDriftReport() {
    return api.get('/admin/acl/capabilities/missing-from-materialized-tenants')
}

/**
 * Propagate template capabilities into a specific tenant's materializations.
 * @param {number|string} tenantId
 * @param {object}        [body]  Optional `{reason}` payload.
 * @returns {Promise<AxiosResponse<{tenant_id: number, added: object[]}>>}
 */
export function propagateToTenant(tenantId, body = undefined) {
    return body === undefined
        ? api.post(`/admin/acl/capabilities/propagate-to-tenant/${tenantId}`)
        : api.post(`/admin/acl/capabilities/propagate-to-tenant/${tenantId}`, body)
}

// ---------------------------------------------------------------------------
// Grant authority
// ---------------------------------------------------------------------------

/**
 * List grant-authority rows. NOTE: returns a bare array, not an envelope.
 * @returns {Promise<AxiosResponse<object[]>>}
 */
export function listGrantAuthority() {
    return api.get('/admin/acl/grant-authority')
}

/**
 * Create a grant-authority row (who can grant what).
 * @param {object} body  `{capability_id, granter_capability_id, reason}`
 * @returns {Promise<AxiosResponse<object>>}
 */
export function createGrantAuthority(body) {
    return api.post('/admin/acl/grant-authority', body)
}

/**
 * Delete a grant-authority row.
 * @param {number|string} id
 * @param {object}        [body]  Optional `{reason}` payload.
 * @returns {Promise<AxiosResponse<void>>}
 */
export function deleteGrantAuthority(id, body = undefined) {
    return body === undefined
        ? api.delete(`/admin/acl/grant-authority/${id}`)
        : api.delete(`/admin/acl/grant-authority/${id}`, { data: body })
}

// ---------------------------------------------------------------------------
// Account overrides + effective
// ---------------------------------------------------------------------------

/**
 * Effective capabilities for an account, with source attribution per row.
 * NOTE: returns a bare array, not an envelope.
 * @param {number|string} accountId
 * @returns {Promise<AxiosResponse<Array<{key: string, source: string, source_id: number}>>>}
 */
export function getAccountEffectiveCapabilities(accountId) {
    return api.get(`/admin/acl/accounts/${accountId}/effective`)
}

/**
 * List override rows for an account.
 * @param {number|string} accountId
 * @returns {Promise<AxiosResponse<{overrides: object[]}>>}
 */
export function listAccountOverrides(accountId) {
    return api.get(`/admin/acl/accounts/${accountId}/overrides`)
}

/**
 * Create an account-level capability override.
 * @param {number|string} accountId
 * @param {object} body  `{capability_id, effect, reason, expires_at?}`
 * @returns {Promise<AxiosResponse<{override: object}>>}
 */
export function createAccountOverride(accountId, body) {
    return api.post(`/admin/acl/accounts/${accountId}/overrides`, body)
}

/**
 * Patch an account-level override.
 * @param {number|string} accountId
 * @param {number|string} overrideId
 * @param {object} body  `{effect?, expires_at?, reason}`
 * @returns {Promise<AxiosResponse<{override: object}>>}
 */
export function patchAccountOverride(accountId, overrideId, body) {
    return api.patch(`/admin/acl/accounts/${accountId}/overrides/${overrideId}`, body)
}

/**
 * Delete an account-level override. The backend requires a reason; we still
 * support the bare-DELETE form for caller-bug safety (will get a 4xx then).
 * @param {number|string} accountId
 * @param {number|string} overrideId
 * @param {object}        [body]  `{reason}` payload (required by backend).
 * @returns {Promise<AxiosResponse<void>>}
 */
export function deleteAccountOverride(accountId, overrideId, body = undefined) {
    return body === undefined
        ? api.delete(`/admin/acl/accounts/${accountId}/overrides/${overrideId}`)
        : api.delete(`/admin/acl/accounts/${accountId}/overrides/${overrideId}`, { data: body })
}

// ---------------------------------------------------------------------------
// Audit log (SAFRS JSON:API)
// ---------------------------------------------------------------------------

/**
 * List audit log rows. Returns JSON:API envelope `{data, meta, links}`.
 *
 * Hits the renamed SAFRS resource `/audit_log/` (was `/acl_audit_log/`
 * pre route-fork PR-2 — `route_fork_schema_008` migration renamed the
 * table and the SAFRS resource path went with it).
 *
 * @param {object} [params]  Optional query params forwarded under axios `{params}`.
 * @returns {Promise<AxiosResponse<{data: object[], meta: object, links: object}>>}
 */
export function listAuditLog(params = undefined) {
    return params === undefined
        ? api.get('/audit_log/')
        : api.get('/audit_log/', { params })
}
